import {
  Box,
  BoxStyleProps,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogHeader,
  Icon,
  Text,
  TextLink,
  useToast,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { useState } from 'react';
import links from '../../constants/links';

import { ReactComponent as CrossCircleSvg } from '@frontend/icons/cross-circle.svg';

import { useExpireBill } from '../../hooks';

interface ClosePaymentProps extends BoxStyleProps {
  className?: string;
  isOffRamp: boolean;
  onComplete?: () => void;
}

export default function ClosePayment({
  className,
  isOffRamp,
  onComplete,
  ...boxProps
}: ClosePaymentProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const { expireBill, loading: expireBillLoading } = useExpireBill();

  const closeModal = () => {
    setOpen(false);
  };

  const openModal = () => {
    setOpen(true);
  };

  const handleExpireBill = async () => {
    try {
      expireBill({
        variables: {
          id: getIdFromUrl(),
          reason: 'buyer_cancellation',
        },
      });
    } catch (error) {
      toast.open({
        title: 'Something went wrong',
        message: 'Please try again later',
        status: 'error',
      });
    } finally {
      setOpen(false);
      onComplete?.();
    }
  };

  return (
    <>
      <Box className={className} {...boxProps}>
        <Button
          onClick={openModal}
          type="button"
          style={{ whiteSpace: 'nowrap' }}
          size="small"
          rightIcon={<Icon component={<CrossCircleSvg />} />}
          data-test-id="closePayment"
        >
          Cancel {isOffRamp ? 'Off Ramp' : 'Payment'}
        </Button>
      </Box>
      {open && (
        <Dialog onRequestClose={closeModal}>
          <DialogHeader description="This will make you lose any progress you’ve made.">
            Cancel {isOffRamp ? 'Off Ramp' : 'Payment'}?
          </DialogHeader>
          <DialogBody>
            <Text
              size="small"
              color="neutral-800"
              data-test-id="refundConfirmationText"
            >
              You will receive an email for the transactions that meet{' '}
              <TextLink
                size="inherit"
                href={links.support.REFUND_POLICY}
                target="_blank"
                rel="noopener noreferrer"
              >
                our refund policy.
              </TextLink>
            </Text>
          </DialogBody>
          <DialogActions>
            <Button
              size="medium"
              type="button"
              variant="secondary"
              onClick={closeModal}
              data-test-id="closeModal"
            >
              Stay
            </Button>
            <Button
              size="medium"
              type="button"
              variant="danger"
              loading={expireBillLoading}
              onClick={handleExpireBill}
              data-test-id="expireBill"
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
